import React, { useState, useRef, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import * as api from '../../services/api.js'
import AppSelect from '../../components/AppSelect.jsx'

const DIFFICULTY_OPTIONS = [
  { value: 'Easy',   label: 'Easy' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Hard',   label: 'Hard' },
]

const INGREDIENT_TYPE_OPTIONS = [
  { value: 'main',     label: 'Main' },
  { value: 'optional', label: 'Optional' },
]

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(null)
  const display = hovered ?? value
  return (
    <div style={{ display: 'flex', gap: 3 }} onMouseLeave={() => setHovered(null)}>
      {[1, 2, 3, 4, 5].map(n => (
        <span
          key={n}
          style={{ cursor: 'pointer', fontSize: '1.2rem', color: n <= display ? 'var(--gold)' : 'var(--stone)', lineHeight: 1 }}
          onMouseEnter={() => setHovered(n)}
          onClick={() => onChange(value === n ? 0 : n)}
        >
          {n <= display ? '★' : '☆'}
        </span>
      ))}
    </div>
  )
}

function newIngredient() {
  return { id: Date.now() + Math.random(), name: '', amount: '', type: 'main', notes: '' }
}

function emptyRecipe() {
  return {
    id: Date.now(),
    name: '',
    image: '',
    description: '',
    cuisine: '',
    servings: '',
    prepTime: '',
    cookTime: '',
    ingredients: [newIngredient()],
    steps: '',
    tips: '',
    rating: 0,
  }
}

function RecipeCard({ recipe, onUpdate, onDelete, onAddToShoppingList }) {
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [draft, setDraft] = useState(recipe)
  const fileRef = useRef()

  function handleImageChange(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => setDraft(d => ({ ...d, image: ev.target.result }))
    reader.readAsDataURL(file)
  }

  function addIngredient() {
    setDraft(d => ({ ...d, ingredients: [...d.ingredients, newIngredient()] }))
  }

  function updateIngredient(id, field, value) {
    setDraft(d => ({
      ...d,
      ingredients: d.ingredients.map(i => i.id === id ? { ...i, [field]: value } : i),
    }))
  }

  function removeIngredient(id) {
    setDraft(d => ({ ...d, ingredients: d.ingredients.filter(i => i.id !== id) }))
  }

  function save() {
    onUpdate(draft)
    setEditing(false)
  }

  function cancelEdit() {
    setDraft(recipe)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="recipe-card editing">
        <div className="recipe-image-wrap" onClick={() => fileRef.current.click()}>
          {draft.image
            ? <img src={draft.image} alt="" />
            : (
              <div className="recipe-img-placeholder edit-mode">
                <span style={{ fontSize: '1.5rem' }}>📷</span>
                <span>Click to add image</span>
              </div>
            )
          }
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageChange} />
        </div>

        <div className="recipe-edit-body">
          <div className="cook-form">
            <div className="full">
              <label className="field-label">Dish Name</label>
              <input
                className="field-input"
                value={draft.name}
                onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                placeholder="e.g. Lemon Butter Pasta"
              />
            </div>
            <div className="full">
              <label className="field-label">Description</label>
              <input
                className="field-input"
                value={draft.description}
                onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                placeholder="A short description of the dish"
              />
            </div>
            <div>
              <label className="field-label">Cuisine</label>
              <input
                className="field-input"
                value={draft.cuisine}
                onChange={e => setDraft(d => ({ ...d, cuisine: e.target.value }))}
                placeholder="e.g. Italian"
              />
            </div>
            <div>
              <label className="field-label">Servings</label>
              <input
                className="field-input"
                value={draft.servings}
                onChange={e => setDraft(d => ({ ...d, servings: e.target.value }))}
                placeholder="e.g. 4"
              />
            </div>
            <div>
              <label className="field-label">Prep time (mins)</label>
              <input
                className="field-input"
                value={draft.prepTime}
                onChange={e => setDraft(d => ({ ...d, prepTime: e.target.value }))}
                placeholder="15"
              />
            </div>
            <div>
              <label className="field-label">Cook time (mins)</label>
              <input
                className="field-input"
                value={draft.cookTime}
                onChange={e => setDraft(d => ({ ...d, cookTime: e.target.value }))}
                placeholder="20"
              />
            </div>
          </div>

          <div style={{ margin: '1rem 0 0.5rem' }}>
            <label className="field-label" style={{ marginBottom: '8px', display: 'block' }}>
              Ingredients
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {draft.ingredients.map(ing => (
                <div key={ing.id} className="ingredient-row">
                  <input
                    className="field-input"
                    style={{ width: '90px', flexShrink: 0 }}
                    placeholder="Amount"
                    value={ing.amount}
                    onChange={e => updateIngredient(ing.id, 'amount', e.target.value)}
                  />
                  <input
                    className="field-input"
                    style={{ flex: 1, minWidth: '80px' }}
                    placeholder="Ingredient"
                    value={ing.name}
                    onChange={e => updateIngredient(ing.id, 'name', e.target.value)}
                  />
                  <div style={{ width: '110px', flexShrink: 0 }}>
                    <AppSelect
                      value={ing.type}
                      onChange={v => updateIngredient(ing.id, 'type', v)}
                      options={INGREDIENT_TYPE_OPTIONS}
                    />
                  </div>
                  <input
                    className="field-input"
                    style={{ flex: 1, minWidth: '80px' }}
                    placeholder="Notes (e.g. finely chopped)"
                    value={ing.notes}
                    onChange={e => updateIngredient(ing.id, 'notes', e.target.value)}
                  />
                  <button
                    className="book-delete"
                    style={{ opacity: 1 }}
                    onClick={() => removeIngredient(ing.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button className="ghost-btn" style={{ alignSelf: 'flex-start' }} onClick={addIngredient}>
                + Add ingredient
              </button>
            </div>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <label className="field-label">Steps / Method</label>
            <textarea
              className="field-textarea"
              rows={6}
              value={draft.steps}
              onChange={e => setDraft(d => ({ ...d, steps: e.target.value }))}
              placeholder={'1. Bring a large pan of salted water to the boil…\n2. …'}
            />
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <label className="field-label">Tips & Notes</label>
            <textarea
              className="field-textarea"
              rows={2}
              value={draft.tips}
              onChange={e => setDraft(d => ({ ...d, tips: e.target.value }))}
              placeholder="Any tips or tricks specific to this dish"
            />
          </div>

          <div style={{ marginTop: '0.75rem' }}>
            <label className="field-label" style={{ marginBottom: '6px', display: 'block' }}>Your Rating</label>
            <StarRating value={draft.rating} onChange={r => setDraft(d => ({ ...d, rating: r }))} />
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <button className="save-btn ready" onClick={save}>Save Recipe</button>
            <button className="ghost-btn" onClick={cancelEdit}>Cancel</button>
            <button
              className="book-delete"
              style={{ opacity: 1, marginLeft: 'auto', fontSize: '0.78rem' }}
              onClick={() => onDelete(recipe.id)}
            >
              Delete recipe
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="recipe-card">
      {recipe.image
        ? <img src={recipe.image} alt={recipe.name} className="recipe-img" />
        : <div className="recipe-img-placeholder no-image">No image</div>
      }
      <div className="recipe-card-body">
        <div className="recipe-card-header">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="recipe-name">{recipe.name || 'Untitled recipe'}</h3>
            {recipe.cuisine && <span className="recipe-chip">{recipe.cuisine}</span>}
            {recipe.description && <p className="recipe-desc">{recipe.description}</p>}
          </div>
          <button className="ghost-btn" style={{ flexShrink: 0 }} onClick={() => setEditing(true)}>Edit</button>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', margin: '0.5rem 0 0.25rem' }}>
          {recipe.servings && <span className="recipe-meta-item">Serves {recipe.servings}</span>}
          {recipe.prepTime && <span className="recipe-meta-item">Prep {recipe.prepTime}m</span>}
          {recipe.cookTime && <span className="recipe-meta-item">Cook {recipe.cookTime}m</span>}
        </div>

        {recipe.rating > 0 && (
          <div style={{ display: 'flex', gap: 2, margin: '0.25rem 0' }}>
            {[1, 2, 3, 4, 5].map(n => (
              <span key={n} style={{ fontSize: '0.95rem', color: n <= recipe.rating ? 'var(--gold)' : 'var(--stone)' }}>
                {n <= recipe.rating ? '★' : '☆'}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: '6px', marginTop: '0.6rem', flexWrap: 'wrap' }}>
          <button
            className="ghost-btn"
            style={{ fontSize: '0.75rem' }}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? 'Hide ▲' : 'Show details ▼'}
          </button>
          {recipe.ingredients.some(i => i.name) && (
            <button
              className="ghost-btn"
              style={{ fontSize: '0.75rem' }}
              onClick={() => onAddToShoppingList(recipe.ingredients)}
            >
              + Shopping list
            </button>
          )}
        </div>

        {expanded && (
          <div style={{ marginTop: '0.75rem' }}>
            {recipe.ingredients.some(i => i.name) && (
              <>
                <p className="field-label" style={{ marginBottom: '0.4rem' }}>Ingredients</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 0.75rem', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {recipe.ingredients.filter(i => i.name).map(ing => (
                    <li key={ing.id} style={{ fontSize: '0.85rem', color: 'var(--ink)', display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                      {ing.amount && (
                        <strong style={{ color: 'var(--bark)', flexShrink: 0, minWidth: '55px' }}>{ing.amount}</strong>
                      )}
                      <span>{ing.name}</span>
                      {ing.type === 'optional' && (
                        <span className="recipe-chip" style={{ fontSize: '0.65rem' }}>optional</span>
                      )}
                      {ing.notes && (
                        <span style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.78rem' }}>{ing.notes}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {recipe.steps && (
              <>
                <p className="field-label" style={{ marginBottom: '0.4rem' }}>Method</p>
                <div style={{ fontSize: '0.85rem', color: 'var(--ink)', lineHeight: '1.75', whiteSpace: 'pre-wrap', marginBottom: '0.75rem' }}>
                  {recipe.steps}
                </div>
              </>
            )}

            {recipe.tips && (
              <div className="tip-box">
                <p className="tip-box-title">Tips & Notes</p>
                <p>{recipe.tips}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Full add-recipe form ──────────────────────────────────────────────────────

function emptyFormState() {
  return {
    title: '',
    prepTime: '',
    cookTime: '',
    servings: '',
    cuisine: '',
    difficulty: 'Easy',
    ingredientsText: '',
    steps: '',
    tips: '',
  }
}

function formToRecipe(form) {
  const ingredientLines = form.ingredientsText
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean)
  const ingredients = ingredientLines.length
    ? ingredientLines.map(line => ({ id: Date.now() + Math.random(), name: line, amount: '', type: 'main', notes: '' }))
    : [newIngredient()]

  return {
    ...emptyRecipe(),
    name: form.title.trim(),
    cuisine: form.cuisine.trim(),
    servings: form.servings,
    prepTime: form.prepTime,
    cookTime: form.cookTime,
    steps: form.steps,
    tips: form.tips,
    ingredients,
  }
}

function AddRecipeForm({ onSave, onCancel, initialForm = {} }) {
  const [form, setForm] = useState({ ...emptyFormState(), ...initialForm })
  const update = (field, value) => setForm(f => ({ ...f, [field]: value }))

  return (
    <div className="add-recipe-form">
      <div className="add-recipe-form__header">
        <span className="add-recipe-form__title">Add a recipe</span>
        <button className="add-recipe-form__close" onClick={onCancel}>✕</button>
      </div>

      <div className="form-group">
        <label className="form-label">Recipe name *</label>
        <input
          type="text"
          className="form-input"
          value={form.title}
          onChange={e => update('title', e.target.value)}
          placeholder="e.g. Grandma's lasagne"
          autoFocus
          maxLength={100}
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Prep time</label>
          <input type="text" className="form-input" value={form.prepTime} onChange={e => update('prepTime', e.target.value)} placeholder="e.g. 20 min" maxLength={20} />
        </div>
        <div className="form-group">
          <label className="form-label">Cook time</label>
          <input type="text" className="form-input" value={form.cookTime} onChange={e => update('cookTime', e.target.value)} placeholder="e.g. 45 min" maxLength={20} />
        </div>
        <div className="form-group">
          <label className="form-label">Serves</label>
          <input type="text" className="form-input" value={form.servings} onChange={e => update('servings', e.target.value)} placeholder="4" maxLength={10} />
        </div>
      </div>

      <div className="form-row-2">
        <div className="form-group">
          <label className="form-label">Cuisine</label>
          <input type="text" className="form-input" value={form.cuisine} onChange={e => update('cuisine', e.target.value)} placeholder="e.g. Italian" maxLength={40} />
        </div>
        <div className="form-group">
          <label className="form-label">Difficulty</label>
          <AppSelect value={form.difficulty} onChange={v => update('difficulty', v)} options={DIFFICULTY_OPTIONS} />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          Ingredients
          <span className="form-label-hint"> — one per line</span>
        </label>
        <textarea
          className="form-textarea"
          rows={6}
          value={form.ingredientsText}
          onChange={e => update('ingredientsText', e.target.value)}
          placeholder={"200g pasta\n2 garlic cloves\n1 tbsp olive oil\n..."}
        />
      </div>

      <div className="form-group">
        <label className="form-label">
          Method
          <span className="form-label-hint"> — one step per paragraph</span>
        </label>
        <textarea
          className="form-textarea"
          rows={7}
          value={form.steps}
          onChange={e => update('steps', e.target.value)}
          placeholder={"Boil the pasta until al dente.\n\nMeanwhile, fry the garlic in oil...\n\n..."}
        />
      </div>

      <div className="form-group">
        <label className="form-label">Notes & tips</label>
        <textarea
          className="form-textarea"
          rows={3}
          value={form.tips}
          onChange={e => update('tips', e.target.value)}
          placeholder="What makes this recipe special? Any tips?"
        />
      </div>

      <div className="add-recipe-form__actions">
        <button
          className="save-btn ready"
          onClick={() => form.title.trim() && onSave(formToRecipe(form))}
          disabled={!form.title.trim()}
          style={{ opacity: form.title.trim() ? 1 : 0.5 }}
        >
          Save recipe
        </button>
        <button className="ghost-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CookingRecipes({ onAddToShoppingList, preFill, onPreFillConsumed }) {
  const [recipes, setRecipes] = useState([])
  const [adding,  setAdding]  = useState(false)
  const [initialForm, setInitialForm] = useState({})

  useEffect(() => { api.getCookingRecipes().then(d => setRecipes(d || [])) }, [])

  useEffect(() => {
    if (!preFill) return
    // Map inspiration recipe format → form state
    const form = {
      title:           preFill.title || preFill.name || '',
      cuisine:         preFill.type  || preFill.cuisine || '',
      prepTime:        preFill.prepTime  || '',
      cookTime:        preFill.cookTime  || '',
      servings:        preFill.servings  ? String(preFill.servings) : '',
      difficulty:      preFill.difficulty || 'Easy',
      ingredientsText: Array.isArray(preFill.ingredients)
        ? preFill.ingredients.join('\n')
        : (preFill.ingredientsText || ''),
      steps: Array.isArray(preFill.method)
        ? preFill.method.join('\n\n')
        : (preFill.steps || ''),
      tips: preFill.notes || preFill.tips || '',
    }
    setInitialForm(form)
    setAdding(true)
    if (onPreFillConsumed) onPreFillConsumed()
  }, [preFill])

  function updateRecipes(updater) {
    setRecipes(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      api.saveCookingRecipes(next)
      return next
    })
  }

  function handleSave(recipe) {
    updateRecipes(prev => [recipe, ...prev])
    setAdding(false)
    setInitialForm({})
  }

  function handleCancel() {
    setAdding(false)
    setInitialForm({})
  }

  function updateRecipe(updated) {
    updateRecipes(prev => prev.map(r => r.id === updated.id ? updated : r))
  }

  function deleteRecipe(id) {
    updateRecipes(prev => prev.filter(r => r.id !== id))
  }

  return (
    <>
      <Card label="Cooking" title="My Recipes">
        {!adding ? (
          <button className="save-btn ready" onClick={() => setAdding(true)}>+ Add Recipe</button>
        ) : null}
      </Card>

      {adding && (
        <AddRecipeForm onSave={handleSave} onCancel={handleCancel} initialForm={initialForm} />
      )}

      {recipes.length === 0 && !adding && (
        <Card label="Recipes" title="">
          <p style={{ color: 'var(--stone)', fontStyle: 'italic', fontSize: '0.88rem' }}>
            No recipes yet — add your first one above!
          </p>
        </Card>
      )}

      <div className="recipe-grid">
        {recipes.map(recipe => (
          <RecipeCard
            key={recipe.id}
            recipe={recipe}
            onUpdate={updateRecipe}
            onDelete={deleteRecipe}
            onAddToShoppingList={onAddToShoppingList}
          />
        ))}
      </div>
    </>
  )
}
